<template>
  <v-dialog :value="value" max-width="600px" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ formTitle }}</v-card-title>
      <v-card-text>
        <ValidationObserver ref="formObserver">
          <v-form :disabled="isLoading">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <ValidationProvider name="Name" rules="required|max:128">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.name" label="Name*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <upload-file accept="image/*" rules="imageFile" label="Logo"
                               upload-type="company_logo"
                               @uploadSuccess="item.logo = $event.url"
                  />
                  <ValidationProvider name="Logo URL" rules="url">
                    <template v-slot="{ errors }">
                      <v-text-field
                        v-model="item.logo"
                        label="Logo URL"
                        :error-messages="errors"
                        :readonly="!isAdmin"
                        :clearable="isAdmin"
                      />
                    </template>
                  </ValidationProvider>
                  <v-img-ex v-if="item.logo && item.logo.length"
                            :src="item.logo"
                            max-width="200"
                            max-height="100"
                            class="mt-2 mb-2"
                            style="cursor:pointer"
                            @click.native="openImageOverlay(item.logo)"
                  />
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="Address line" rules="required|max:128">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.address.addressLine" label="Address Line*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="City" rules="required|max:64">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.address.city" label="City*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="Country" rules="required|max:64">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.address.country" label="Country*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="6">
                  <ValidationProvider name="Telephone" rules="required|regex:/^[+0-9 -]+$/">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.telephone" label="Telephone*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="Email" rules="required|email|max:128">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.email" label="Email*" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider name="Technology used" rules="max:128">
                    <template v-slot="{ errors }">
                      <v-text-field v-model="item.technologyUsed" label="Technology used" :error-messages="errors" />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="6">
                  <ValidationProvider name="Facility type" rules="required">
                    <template v-slot="{ errors }">
                      <v-select
                        v-model="item.facilityType"
                        label="Facility type*"
                        :items="facilityTypeOptions"
                        :error-messages="errors"
                      />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col cols="6">
                  <ValidationProvider name="Number of Coaches" rules="required|numeric">
                    <template v-slot="{ errors }">
                      <v-text-field
                        v-model="item.numberOfCoaches"
                        label="Number of Coaches*"
                        type="number"
                        min="0"
                        :error-messages="errors"
                      />
                    </template>
                  </ValidationProvider>
                </v-col>

                <v-col v-if="item.headCoach" cols="12">
                  <v-text-field
                    v-model="item.headCoach"
                    label="Head Coach"
                    readonly
                    outlined
                    disabled
                  />
                </v-col>

                <v-col v-if="item.id" cols="12">
                  <v-card outlined class="academy-content-pack-panel">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                      Content Packs
                    </v-card-title>
                    <v-card-text class="py-2">
                      <v-list v-if="academyContentPacks.length" dense class="py-0">
                        <v-list-item class="academy-content-pack-header px-0">
                          <v-list-item-content>
                            <v-list-item-title class="text-caption font-weight-bold">
                              Content Pack
                            </v-list-item-title>
                          </v-list-item-content>
                          <div class="academy-content-pack-purchased text-caption font-weight-bold">
                            Purchased
                          </div>
                          <v-list-item-action />
                        </v-list-item>
                        <v-list-item v-for="contentPack in academyContentPacks" :key="contentPack.id">
                          <v-list-item-content>
                            <v-list-item-title>{{ contentPack.name }}</v-list-item-title>
                          </v-list-item-content>
                          <div class="academy-content-pack-purchased text-body-2">
                            {{ formatPurchased(contentPack.purchased) }}
                          </div>
                          <v-list-item-action>
                            <v-btn icon small @click="removeContentPack(contentPack.id)">
                              <v-icon small>
                                {{ icons.mdiDeleteOutline }}
                              </v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                      <div v-else class="text-body-2 grey--text">
                        No content packs assigned.
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </ValidationObserver>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">
          Cancel
        </v-btn>
        <v-btn text color="primary" :loading="isLoading" @click="onSave">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { mdiDeleteOutline } from '@mdi/js'
import { ref, onMounted, watch } from 'vue'
import UploadFile from '@/layouts/components/UploadFile.vue'
import VImgEx from '@/components/VImgEx.vue'
import useImageLightBox from '@/composables/imageLightBox'
import useAuth from '@/composables/useAuth'
import useContentPackAcademies from '@/composables/api/contentPackAcademies'

extend('required', value => !!value || 'This field is required.')
extend('max', (value, args) => {
  if (!value) return true

  return value.length <= args.length || `Maximum length is ${args.length} characters.`
})
extend('regex', (value, args) => {
  const regex = new RegExp(args.regex)

  return regex.test(value) || 'Invalid format.'
})
extend('email', value => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return emailRegex.test(value) || 'Invalid email address.'
})
extend('numeric', value => {
  if (!value) return true

  return /^\d+$/.test(value) || 'This field must be numeric.'
})
extend('url', value => {
  if (!value) return true
  const urlRegex = /^https?:\/\/.+/

  return urlRegex.test(value) || 'Invalid URL format.'
})
extend('k72url', value => {
  if (!value) return true

  return value.includes('k72') || 'URL must be from K72 system.'
})
extend('imageFile', value => {
  if (!value || !value.length) return true
  const file = value[0] || value
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  return allowedTypes.includes(file.type) || 'File must be an image (JPEG, PNG, GIF, or WebP).'
})

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    UploadFile,
    VImgEx,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'save', 'cancel'],
  setup(props, { emit }) {
    const { openImageOverlay } = useImageLightBox()
    const { hasRole } = useAuth()
    const isAdmin = hasRole(['ADMIN'])
    const { fetchContentPacksByAcademyId, deleteContentPackAcademy, formatPurchased } = useContentPackAcademies()
    const academyContentPacks = ref([])

    const loadContentPacks = async () => {
      if (!props.item?.id) {
        academyContentPacks.value = []

        return
      }

      academyContentPacks.value = await fetchContentPacksByAcademyId(props.item.id)
    }

    onMounted(loadContentPacks)
    watch(() => props.item?.id, loadContentPacks)

    const removeContentPack = async contentPackId => {
      await deleteContentPackAcademy(props.item.id, contentPackId)
      academyContentPacks.value = academyContentPacks.value.filter(item => item.id !== contentPackId)
      emit('relations-updated')
    }

    return {
      openImageOverlay,
      isAdmin,
      academyContentPacks,
      removeContentPack,
      formatPurchased,
      icons: {
        mdiDeleteOutline,
      },
    }
  },
  computed: {
    formTitle() {
      return this.item.id ? 'Edit Academy' : 'Add New Academy'
    },
    facilityTypeOptions() {
      return [
        'Golf course',
        'Driving range',
        'Indoor',
        'Other',
      ]
    },
  },
  methods: {
    async onSave() {
      const isValid = await this.$refs.formObserver.validate()
      if (isValid) {
        const itemToSave = { ...this.item }
        delete itemToSave.headCoach
        this.$emit('save', itemToSave)
      }
    },
    onCancel() {
      this.$emit('cancel')
    },
  },
}
</script>

<style scoped>
.academy-content-pack-panel {
  border-color: #d9d9e8 !important;
}

.academy-content-pack-header {
  min-height: 28px;
}

.academy-content-pack-purchased {
  min-width: 72px;
  text-align: center;
}
</style>
